import { RedirectError } from 'src/shared/error';

import type { PromotePageMainData } from '../../types';
import type { PromotePageMainDataQuery } from '../getPromotePageMainData.graphql';

import { normalizePetition } from './normalizePetition';
import { normalizeUser } from './normalizeUser';

function redirectNotFound() {
	return new RedirectError({
		url: '/',
		reason: 'promote_page_data_not_found',
	});
}
function redirectNotPromotable(slug: string) {
	return new RedirectError({
		url: `/p/${encodeURIComponent(slug)}`,
		reason: 'promote_page_accessed_for_non_promotable_petition',
	});
}

export function normalize(data: PromotePageMainDataQuery): PromotePageMainData {
	if (!data.petition) throw redirectNotFound();

	if (!data.petition.promotionSettings) throw redirectNotFound();
	if (!data.petition.promotionSettings.cpm) throw redirectNotFound();
	if (!data.petition.promotionSettings.promotionToken) throw redirectNotFound();

	if (!data.session.country.currency) throw redirectNotFound();

	if (!data.session.country.promotionCountrySettings) throw redirectNotFound();

	if (!data.petition.promotable) throw redirectNotPromotable(data.petition.slug);

	const { user, userProtectedData } = data.session;

	return {
		petition: normalizePetition(data.petition),
		currency: {
			code: data.session.country.currency.code,
			exchangeRate: data.session.country.currency.exchangeRate,
		},
		promotionSettings: {
			cpm: data.petition.promotionSettings.cpm,
			promotionToken: data.petition.promotionSettings.promotionToken,
			bonusCpm: data.petition.promotionSettings.bonusCpm,
			bonusThreshold: data.petition.promotionSettings.bonusThreshold,
		},
		promotionCountrySettings: {
			amountChoices: data.session.country.promotionCountrySettings.amountChoices,
			anchorAmount: data.session.country.promotionCountrySettings.anchorAmount,
			minimumAmount: data.session.country.promotionCountrySettings.minimumAmount,
			maximumAmount: data.session.country.promotionCountrySettings.maximumAmount,
			confirmationAmount: data.session.country.promotionCountrySettings.confirmationAmount,
		},
		membershipProgram: data.session.country.membershipProgram,
		user: normalizeUser({ user, userProtectedData }),
		guestUserData: data.session.guestUserData,
		countryOrStateSignatureCounts: {
			totalSignatureCount: data.countryOrStateSignatureCounts.totalSignatureCount,
		},
		ppmuSunriseDoubleHoldoutExperiment: {
			variant: data.session.experiments.revenuePpmuSunriseDoubleHoldoutExperiment.variation,
		},
		koiPpmuDesignExperiment: {
			variant: data.session.experiments.koiPpmuDesign.variation,
		},
		revenueBanditsPsfComboButtonAmounts: data.featureConfigs.revenueBanditsPsfComboExperiment?.enabled
			? data.featureConfigs.revenueBanditsPsfComboExperiment.contextual_button_amounts
			: null,
		featureConfigs: data.featureConfigs,
	};
}
